/**
 * Light Theme Configuration
 * 
 * Ant Design theme configuration for light mode.
 * Uses default algorithm with custom brand colors.
 * Updated: 2025-12-10 - Enhanced text contrast for better readability
 */

import type { ThemeConfig } from 'antd';
import { sharedTokens } from './shared';

export const lightTheme: ThemeConfig = {
  token: {
    ...sharedTokens,
    
    // Brand Colors
    colorPrimary: '#1890ff',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorInfo: '#1890ff',
    colorLink: '#1890ff',
    
    // Text Colors - Increased opacity for better readability
    colorText: 'rgba(0, 0, 0, 0.95)',
    colorTextSecondary: 'rgba(0, 0, 0, 0.80)',
    colorTextTertiary: 'rgba(0, 0, 0, 0.60)',
    colorTextQuaternary: 'rgba(0, 0, 0, 0.40)',
    
    // Background Colors
    colorBgBase: '#ffffff',
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgLayout: '#f5f5f5',
    colorBgSpotlight: 'rgba(0, 0, 0, 0.85)',
    colorBgMask: 'rgba(0, 0, 0, 0.45)',
    
    // Border Colors
    colorBorder: '#d9d9d9',
    colorBorderSecondary: '#f0f0f0',
    
    // Fill Colors (for backgrounds with slight transparency)
    colorFill: 'rgba(0, 0, 0, 0.15)',
    colorFillSecondary: 'rgba(0, 0, 0, 0.06)',
    colorFillTertiary: 'rgba(0, 0, 0, 0.04)',
    colorFillQuaternary: 'rgba(0, 0, 0, 0.02)',
  },
  
  components: {
    Layout: {
      headerBg: '#ffffff',
      headerColor: '#1d2147',
      headerHeight: 64,
      headerPadding: '0 24px',
      footerBg: '#ffffff',
      footerPadding: '16px 0',
      siderBg: '#ffffff',
      bodyBg: '#f5f7fb',
      triggerBg: '#f0f0f0',
      triggerColor: '#1d2147',
    },
    
    Menu: {
      darkItemBg: '#001529',
      darkItemColor: 'rgba(255, 255, 255, 0.65)',
      darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
      darkItemSelectedBg: '#1890ff',
      darkItemSelectedColor: '#ffffff',
    },
    
    Card: {
      colorBgContainer: '#ffffff',
      colorBorderSecondary: '#f0f0f0',
    },
    
    Table: {
      colorBgContainer: '#ffffff',
      colorBorderSecondary: '#f0f0f0',
      headerBg: '#fafafa',
      headerColor: 'rgba(0, 0, 0, 0.95)',
      rowHoverBg: '#f5f5f5',
    },
    
    Button: {
      primaryColor: '#ffffff',
      primaryShadow: '0 2px 0 rgba(5, 145, 255, 0.1)',
    },
    
    Input: {
      colorBgContainer: '#ffffff',
      colorBorder: '#d9d9d9',
      colorTextPlaceholder: 'rgba(0, 0, 0, 0.40)',
      hoverBorderColor: '#4096ff',
      activeBorderColor: '#1890ff',
      activeShadow: '0 0 0 2px rgba(24, 144, 255, 0.1)',
    },
    
    Select: {
      colorBgContainer: '#ffffff',
      colorBorder: '#d9d9d9',
      colorTextPlaceholder: 'rgba(0, 0, 0, 0.40)',
      optionSelectedBg: '#e6f4ff',
    },
    
    Modal: {
      contentBg: '#ffffff',
      headerBg: '#ffffff',
      titleColor: 'rgba(0, 0, 0, 0.95)',
    },
    
    Drawer: {
      colorBgElevated: '#ffffff',
    },
    
    Notification: {
      colorBgElevated: '#ffffff',
      colorBgContainer: '#ffffff',
      colorText: 'rgba(0, 0, 0, 0.95)',
      colorTextSecondary: 'rgba(0, 0, 0, 0.65)',
      colorBorder: '#d9d9d9',
      boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
    },

    Message: {
      contentBg: '#ffffff',
      colorBgContainer: '#ffffff',
      colorText: 'rgba(0, 0, 0, 0.95)',
      colorTextSecondary: 'rgba(0, 0, 0, 0.65)',
      colorBorder: '#d9d9d9',
      boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
    },
  },
};
